import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import {
  isPermanentlyDismissable,
  useDismissedAnnouncements,
  type PortalAnnouncement,
} from "@nodus/announcements";

/**
 * Shared announcement dismissal state for the member portal.
 *
 * Dismissal has two tiers (see ``isPermanentlyDismissable``):
 *  - **Permanent** (non-critical) → persisted in localStorage; stays hidden
 *    across reloads.
 *  - **Session** (critical / emergency / takeover) → in-memory only; re-shows
 *    on reload so an evacuation can never be permanently silenced.
 *
 * This lives in a provider (not local component state) because two sibling
 * surfaces need to agree on it: ``AnnouncementsHost`` renders the
 * banner/corner/fullscreen and dismisses; the header ``AnnouncementsIndicator``
 * lists everything and can **restore** a dismissed announcement (tap to show
 * again). Without a shared store the indicator couldn't bring back what the
 * host hid.
 */
interface DismissalContextValue {
  /** Is this announcement currently hidden from the intrusive surfaces? */
  isDismissed: (a: PortalAnnouncement) => boolean;
  /** Hide it (permanent or session-only depending on its severity/category). */
  dismiss: (a: PortalAnnouncement) => void;
  /** Bring it back on the surfaces — clears both dismissal tiers. */
  restore: (id: number) => void;
}

const DismissalContext = createContext<DismissalContextValue | null>(null);

export function DismissedAnnouncementsProvider({ children }: { children: ReactNode }) {
  const { dismiss: dismissPersistent, restore: restorePersistent, isDismissed: isDismissedPersistent } =
    useDismissedAnnouncements();
  const [sessionDismissed, setSessionDismissed] = useState<Set<number>>(new Set());

  const dismiss = useCallback(
    (a: PortalAnnouncement) => {
      if (isPermanentlyDismissable(a)) {
        dismissPersistent(a.id);
      } else {
        setSessionDismissed((prev) => (prev.has(a.id) ? prev : new Set(prev).add(a.id)));
      }
    },
    [dismissPersistent],
  );

  const restore = useCallback(
    (id: number) => {
      // Clear from BOTH tiers — the caller (indicator) doesn't need to know
      // which one hid it.
      restorePersistent(id);
      setSessionDismissed((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    },
    [restorePersistent],
  );

  const isDismissed = useCallback(
    (a: PortalAnnouncement) =>
      isPermanentlyDismissable(a) ? isDismissedPersistent(a.id) : sessionDismissed.has(a.id),
    [isDismissedPersistent, sessionDismissed],
  );

  const value = useMemo(() => ({ isDismissed, dismiss, restore }), [isDismissed, dismiss, restore]);
  return <DismissalContext.Provider value={value}>{children}</DismissalContext.Provider>;
}

export function useAnnouncementDismissal(): DismissalContextValue {
  const ctx = useContext(DismissalContext);
  if (!ctx) {
    throw new Error("useAnnouncementDismissal must be used within a DismissedAnnouncementsProvider");
  }
  return ctx;
}
